import React, { useRef, useState, useCallback } from "react";
import { Camera, Trash2, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { VoiceTextarea } from "@/components/VoiceInput";
import { toast } from "sonner";

const MAX_PHOTOS = 10;
const MAX_DIMENSION = 1280;       // longest side
const COMPRESSION_QUALITY = 0.78; // JPEG quality

/**
 * Compress an image File into a small base64 data URL.
 * Returns: { dataUrl, width, height, originalSize, compressedSize }
 */
export async function compressImage(file) {
    const reader = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = reader.result;
    });
    let { width, height } = img;
    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);
    const dataUrl = canvas.toDataURL("image/jpeg", COMPRESSION_QUALITY);
    return {
        dataUrl,
        width,
        height,
        originalSize: file.size,
        compressedSize: Math.round((dataUrl.length * 3) / 4),
    };
}

/**
 * PhotoCapture - take/pick photos, compress to ~150KB each, optional caption with voice dictation.
 *
 * Props:
 *  - value: [{ base64, caption }] current photos
 *  - onChange: (next) => void
 *  - max: max photos (default 10)
 */
export function PhotoCapture({ value = [], onChange, max = MAX_PHOTOS }) {
    const inputRef = useRef(null);
    const [processing, setProcessing] = useState(false);

    const handleFiles = useCallback(async (files) => {
        if (!files || files.length === 0) return;
        const slots = max - value.length;
        if (slots <= 0) {
            toast.error(`Máximo ${max} fotos por reporte`);
            return;
        }
        const toProcess = Array.from(files).slice(0, slots);
        if (files.length > slots) {
            toast.warning(`Solo se agregarán ${slots} fotos (límite ${max})`);
        }
        setProcessing(true);
        const added = [];
        for (const f of toProcess) {
            if (!f.type?.startsWith("image/")) {
                toast.error(`${f.name}: formato no soportado`);
                continue;
            }
            try {
                const { dataUrl, compressedSize } = await compressImage(f);
                added.push({ base64: dataUrl, caption: "", _size: compressedSize });
            } catch (e) {
                toast.error("No se pudo procesar la foto");
            }
        }
        if (added.length) {
            onChange([...value, ...added]);
            toast.success(`${added.length} foto${added.length === 1 ? "" : "s"} agregada${added.length === 1 ? "" : "s"}`);
        }
        setProcessing(false);
        if (inputRef.current) inputRef.current.value = "";
    }, [value, max, onChange]);

    const updateCaption = (i, caption) => {
        const next = value.slice();
        next[i] = { ...next[i], caption };
        onChange(next);
    };
    const removePhoto = (i) => {
        const next = value.filter((_, idx) => idx !== i);
        onChange(next);
    };

    const remaining = max - value.length;

    return (
        <div data-testid="photo-capture">
            <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2 mb-0">
                    <ImageIcon className="w-4 h-4 text-[hsl(var(--trulyn-navy))]" />
                    Evidencia fotográfica
                </Label>
                <Badge variant="outline" className="font-mono" data-testid="photo-counter">
                    {value.length}/{max}
                </Badge>
            </div>

            {value.length > 0 && (
                <div className="grid grid-cols-2 gap-3 mb-3">
                    {value.map((ph, i) => (
                        <div key={i} className="border rounded-lg overflow-hidden bg-white" data-testid={`photo-item-${i}`}>
                            <div className="relative aspect-video bg-black/5">
                                <img src={ph.base64} alt={`Foto ${i + 1}`} className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => removePhoto(i)}
                                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600"
                                    data-testid={`photo-remove-${i}`}
                                    title="Eliminar foto"
                                    aria-label={`Eliminar foto ${i + 1}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                                <span className="absolute bottom-1 left-1 text-[10px] font-mono bg-black/60 text-white px-1.5 py-0.5 rounded">
                                    {i + 1}
                                </span>
                            </div>
                            <div className="p-2">
                                <VoiceTextarea
                                    value={ph.caption || ""}
                                    onChange={(v) => updateCaption(i, v)}
                                    placeholder="Descripción (toca el mic para dictar)…"
                                    rows={2}
                                    className="text-xs min-h-0"
                                    testId={`photo-caption-${i}`}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {remaining > 0 ? (
                <>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        className="hidden"
                        onChange={(e) => handleFiles(e.target.files)}
                        data-testid="photo-file-input"
                    />
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => inputRef.current?.click()}
                        className="w-full h-12 border-dashed border-2 hover:border-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10"
                        disabled={processing}
                        data-testid="photo-add-button"
                    >
                        <Camera className="w-5 h-5 mr-2" />
                        {processing ? "Procesando..." : value.length === 0 ? "Tomar / Agregar foto" : `Agregar más (${remaining} disponibles)`}
                    </Button>
                </>
            ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Has alcanzado el límite de {max} fotos.
                </div>
            )}

            <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
                Las fotos se comprimen automáticamente (~150 KB) para optimizar la sincronización offline.
            </p>
        </div>
    );
}

export default PhotoCapture;
